import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layouts/MainLayout';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { DataTable } from '../components/ui/data-table';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MultiSelect } from '../components/ui/multi-select';
import { reviewsAPI, applicationsAPI } from '../lib/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/use-toast';
import type { AccessReview, Application } from '../types';

export const ReviewsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const [reviews, setReviews] = useState<AccessReview[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduledDate: '',
    applicationIds: [] as string[],
  });

  useEffect(() => {
    loadReviews();
    loadApplications();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const allReviews = await reviewsAPI.getAll();

      // Filter out malformed reviews
      const validReviews = allReviews.filter(review =>
        review && Array.isArray(review.applicationIds)
      );

      // If user is superadmin, show all reviews
      if (isSuperAdmin) {
        setReviews(validReviews);
      } else {
        // For app admins, filter reviews to show only those containing apps they manage
        const userEmail = user?.email || '';
        const allApps = await applicationsAPI.getAll();

        // Find all apps where user is an admin
        const userAppIds = allApps
          .filter(app => app.admins.includes(userEmail))
          .map(app => app.id);

        // Filter reviews that contain at least one app the user manages
        const filteredReviews = validReviews.filter(review =>
          review.applicationIds.some(appId => userAppIds.includes(appId))
        );

        setReviews(filteredReviews);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    try {
      const allApps = await applicationsAPI.getAll();

      // If user is superadmin, show all apps
      // If user is app admin, show only their apps
      if (isSuperAdmin) {
        setApplications(allApps);
      } else {
        const userEmail = user?.email || '';
        const myApps = allApps.filter(app => app.admins.includes(userEmail));
        setApplications(myApps);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    }
  };

  const handleCreateReview = async () => {
    if (!formData.title || formData.applicationIds.length === 0) {
      toast({
        title: 'Error',
        description: 'Please provide a title and select at least one application',
        className: 'bg-red-50 border-red-200',
      });
      return;
    }

    try {
      await reviewsAPI.create({
        title: formData.title,
        description: formData.description,
        scheduledDate: formData.scheduledDate || undefined,
        applicationIds: formData.applicationIds,
        status: 'in_progress',
        createdBy: user?.email || '',
        initiatedBy: user?.email || '',
        findings: [],
      });

      setIsCreateDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        scheduledDate: '',
        applicationIds: [],
      });
      await loadReviews();

      toast({
        title: 'Success',
        description: 'Review created successfully',
        className: 'bg-green-50 border-green-200',
      });
    } catch (error) {
      console.error('Error creating review:', error);
      toast({
        title: 'Error',
        description: 'Failed to create review',
        className: 'bg-red-50 border-red-200',
      });
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Access Reviews</h1>
          <Button onClick={() => setIsCreateDialogOpen(true)}>Create New Review</Button>
        </div>

{loading ? (
          <div>Loading reviews...</div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>All Reviews ({reviews.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={reviews}
                columns={[
                  {
                    key: 'title',
                    label: 'Title',
                    sortable: true,
                    render: (review) => (
                      <span className="font-medium">{review.title || `Review ${review.id}`}</span>
                    ),
                  },
                  {
                    key: 'description',
                    label: 'Description',
                    render: (review) => (
                      <span className="text-sm text-muted-foreground">
                        {review.description || 'No description'}
                      </span>
                    ),
                  },
                  {
                    key: 'createdAt',
                    label: 'Created',
                    sortable: true,
                    render: (review) => new Date(review.createdAt).toLocaleDateString(),
                  },
                  {
                    key: 'scheduledDate',
                    label: 'Scheduled',
                    sortable: true,
                    render: (review) =>
                      review.scheduledDate
                        ? new Date(review.scheduledDate).toLocaleDateString()
                        : 'Not scheduled',
                  },
                  {
                    key: 'status',
                    label: 'Status',
                    sortable: true,
                    render: (review) => (
                      <Badge
                        className={
                          review.status === 'completed'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30'
                            : review.status === 'in_progress'
                            ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }
                      >
                        {review.status.replace('_', ' ')}
                      </Badge>
                    ),
                  },
                  {
                    key: 'findings',
                    label: 'Findings',
                    render: (review) => <span className="text-sm">{review.findings?.length || 0}</span>,
                  },
                  {
                    key: 'actions',
                    label: 'Actions',
                    render: (review) => (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/reviews/${review.id}`)}
                      >
                        View Details
                      </Button>
                    ),
                  },
                ]}
                searchKeys={['title', 'description']}
                filters={[
                  {
                    key: 'status',
                    label: 'Status',
                    options: [
                      { value: 'draft', label: 'Draft' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'archived', label: 'Archived' },
                    ],
                  },
                ]}
                emptyMessage="No access reviews found. Create a new review to get started."
                pageSize={25}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Review Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Review</DialogTitle>
            <DialogDescription>
              Enter the details for the new access review.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Q1 2025 Access Review"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Review description..."
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="scheduledDate">Scheduled Date</Label>
              <Input
                id="scheduledDate"
                type="date"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="applications">Applications *</Label>
              <MultiSelect
                options={applications.map((app) => ({
                  value: app.id,
                  label: app.name,
                }))}
                selected={formData.applicationIds}
                onChange={(selected) => setFormData({ ...formData, applicationIds: selected })}
                placeholder="Select applications..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateReview}>Create Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
